import React, { useState, useRef, useEffect } from 'react';
import { copyToClipboard } from '../utils/helpers';
import { UserData } from '../types';
import { useAuth, useAudioPlayerState } from '../contexts/AuthContext';
import { compressImage, uploadMediaFile } from '../utils/mediaUtils';
import api, { updateUserOnServer } from '../api';
import { PfpCropperModal } from './PfpCropperModal';
import { PlaylistModal } from './PlaylistModal';
import { FormattedText } from './FormattedText';
import StickerText, { stickerTextStyle } from './StickerText';
import {
    CameraIcon,
    EditIcon,
    MailIcon,
    MoreHorizontalIcon,
    VolumeOffIcon,
    BrokenHeartIcon,
    PrivateIcon,
    TrashIcon,
    CloseIcon,
    SendIcon,
    PlusIcon,
    BookmarkIcon
} from './Icons';
import { YipYapCreateModal } from './MessageModals';

const hexToRgba = (hex: string, op: number) => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    const r = parseInt(c.substring(0, 2), 16), g = parseInt(c.substring(2, 4), 16), b = parseInt(c.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
};

const getSocialIcon = (key: string) => {
    switch (key) {
        case 'instagram': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <radialGradient id="igRG" cx="35%" cy="105%" r="120%">
                        <stop offset="0%" stopColor="#FFDC80" />
                        <stop offset="30%" stopColor="#f09433" />
                        <stop offset="52%" stopColor="#e6683c" />
                        <stop offset="70%" stopColor="#dc2743" />
                        <stop offset="85%" stopColor="#cc2366" />
                        <stop offset="100%" stopColor="#bc1888" />
                    </radialGradient>
                    <mask id="igMask">
                        <rect width="24" height="24" fill="white" />
                        <circle cx="12" cy="12" r="4.5" fill="none" stroke="black" strokeWidth="1.8" />
                        <circle cx="17.3" cy="6.7" r="1.3" fill="black" />
                    </mask>
                </defs>
                <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4c0 3.2-2.6 5.8-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8C2 4.6 4.6 2 7.8 2z" fill="url(#igRG)" mask="url(#igMask)" />
            </svg>
        );
        case 'tiktok': return (
            <svg viewBox="0 0 24 24" width={30} height={34}>
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z" fill="#010101" />
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-.8a4.83 4.83 0 003.57 4.69z" fill="#EE1D52" />
                <path d="M9.49 15.67a2.89 2.89 0 000 5.78 2.89 2.89 0 002.88-2.5v-9.9a8.18 8.18 0 004 1.1V6.74a4.85 4.85 0 01-4-4.74" stroke="#69C9D0" strokeWidth="0.8" fill="none" />
            </svg>
        );
        case 'whatsapp': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="waMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M17.12 14.74c-.25.7-1.23 1.28-1.74 1.36-.5.08-.96.1-3.07-.64-2.56-1.05-4.18-3.64-4.31-3.81-.12-.17-.99-1.32-.99-2.52s.62-1.78.84-2.02c.22-.24.48-.3.64-.3s.32.01.46.01c.14 0 .34-.05.53.41.19.46.65 1.59.71 1.7.06.12.1.26.02.41-.08.15-.12.24-.24.37-.12.13-.25.29-.36.39-.12.12-.24.24-.1.47.13.23.59.97 1.26 1.57.87.77 1.6.97 1.83 1.08.23.11.36.09.5-.05.13-.14.57-.66.72-.89.15-.22.31-.18.52-.11.21.07 1.33.63 1.56.74.23.11.38.17.44.26.06.09.06.51-.19 1.06z" fill="black" />
                    </mask>
                </defs>
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.38 1.26 4.8L2 22l5.46-1.43a9.8 9.8 0 004.6 1.15c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2z" fill="#25D366" mask="url(#waMask)" />
            </svg>
        );
        case 'facebook': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="fbMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M13.5 21.9V15h2.2l.3-2.5H13.5v-1.6c0-.7.35-1.4 1.4-1.4h1.1V7.2a13 13 0 00-1.9-.17c-1.94 0-3.21 1.18-3.21 3.31V12.5H8.5V15h2.4v6.9a8.5 8.5 0 001.3.1 8.5 8.5 0 001.3-.1z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#1877F2" mask="url(#fbMask)" />
            </svg>
        );
        case 'twitter': return (
            <svg viewBox="0 0 24 24" width={34} height={28}>
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" fill="#1DA1F2" />
            </svg>
        );
        case 'threads': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="threadsMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M16.27 11.47c-.06-.03-1.06-.49-1.49-.49-.03 0-.07 0-.1.01-.12.01-1.36.5-1.36 2.42 0 1.63 1 2.43 2.14 2.43.56 0 1.1-.22 1.53-.6" stroke="black" strokeWidth="1.3" fill="none" strokeLinecap="round" />
                        <path d="M7.5 9c0 4.14 3.36 7.5 7.5 7.5" stroke="black" strokeWidth="1.3" fill="none" strokeLinecap="round" />
                        <path d="M15.3 8c-.57.08-1.15.2-1.7.2C9.86 8.2 7 11 7 14.5" stroke="black" strokeWidth="1.3" fill="none" strokeLinecap="round" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="black" mask="url(#threadsMask)" />
            </svg>
        );
        case 'appleMusic': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <linearGradient id="amG" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#FC5C7D" />
                        <stop offset="100%" stopColor="#6A3093" />
                    </linearGradient>
                    <mask id="amMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M16 5.5H9.5a1 1 0 00-1 1v9a3 3 0 102 2.83V10.5h5v3.17a3 3 0 102 2.83V6.5a1 1 0 00-1-1z" fill="black" />
                    </mask>
                </defs>
                <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4c0 3.2-2.6 5.8-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8C2 4.6 4.6 2 7.8 2z" fill="url(#amG)" mask="url(#amMask)" />
            </svg>
        );
        case 'soundcloud': return (
            <svg viewBox="0 0 32 20" width={36} height={24}>
                <path d="M0 13.5a3 3 0 003 3h19.5a4.5 4.5 0 000-9c-.2 0-.4.01-.6.03A7.5 7.5 0 008 10.5a3.5 3.5 0 00-3 3.4A3 3 0 000 13.5z" fill="#FF5500" />
                <path d="M4 12a.5.5 0 010 1" stroke="#FF5500" fill="none" />
                <path d="M3 9c-1 1.5-1.5 3-1.5 4" stroke="#FF5500" strokeWidth="0.5" fill="none" />
                <path d="M5.5 7C4 8.5 3 10.5 3 12" stroke="#FF5500" strokeWidth="0.5" fill="none" />
            </svg>
        );
        case 'pinterest': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="pinMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M12 3a9 9 0 00-3.6 17.2c0-.8.01-1.7.2-2.5l1.4-5.9s-.35-.7-.35-1.75c0-1.64.96-2.87 2.15-2.87.1 0 1.16.08 1.16 1.38 0 .84-.53 2.1-.81 3.27-.23.98.48 1.77 1.44 1.77 1.73 0 2.9-2.22 2.9-4.85 0-2.53-1.62-4.41-4.53-4.41-3.3 0-5.35 2.47-5.35 5.23 0 .95.27 1.62.7 2.13.2.23.22.32.15.58l-.27 1.07c-.08.3-.31.4-.57.3C5.5 13.1 5 11.4 5 9.93c0-3.65 3.08-8.04 9.2-8.04 4.73 0 7.8 3.45 7.8 7.16 0 4.93-2.73 8.6-6.73 8.6-1.35 0-2.62-1.05-3.06-2.24l-.9 3.5c-.33 1.2-.97 2.4-1.56 3.32.97.28 2 .43 3.05.43A9 9 0 0012 3z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#E60023" mask="url(#pinMask)" />
            </svg>
        );
        case 'youtube': return (
            <svg viewBox="0 0 32 22" width={38} height={26}>
                <path d="M31.3 3.44A4 4 0 0028.47.6C26 0 16 0 16 0S6 0 3.53.6A4 4 0 00.7 3.44C0 5.9 0 11 0 11s0 5.1.7 7.56A4 4 0 003.53 21.4C6 22 16 22 16 22s10 0 12.47-.6a4 4 0 002.83-2.84C32 16.1 32 11 32 11s0-5.1-.7-7.56zM12.8 15.6V6.4L21.07 11 12.8 15.6z" fill="#FF0000" />
            </svg>
        );
        case 'cashapp': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="cashMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M14.1 10.2a3.5 3.5 0 00-2.5-.7c-.9 0-1.4.4-1.4.85 0 .5.48.78 1.5 1.07 1.7.5 3 1.22 3 2.73 0 1.6-1.27 2.48-3.1 2.7V18h-1.4v-1.13c-1-.1-2.1-.52-2.85-1.07l.88-1.15c.7.5 1.5.8 2.37.92.8 0 1.3-.32 1.3-.88 0-.6-.58-.88-1.75-1.27-1.65-.52-2.77-1.2-2.77-2.55 0-1.42 1.1-2.4 2.82-2.63V7h1.4v1.27c.9.1 1.8.43 2.47.93L14.1 10.2z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#00D64F" mask="url(#cashMask)" />
            </svg>
        );
        case 'twitch': return (
            <svg viewBox="0 0 24 28" width={30} height={34}>
                <path d="M5.5 0L1 5v18h6v5l5-5h4l7-7V0H5.5zm14.5 15l-3.5 3.5h-4l-3 3v-3H6V2h14v13z" fill="#9146FF" />
                <rect x="11" y="7" width="2" height="5" fill="#9146FF" />
                <rect x="15" y="7" width="2" height="5" fill="#9146FF" />
            </svg>
        );
        case 'venmo': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="venmoMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M16.5 5.3c.55.9.8 1.85.8 3.1 0 3.85-3.3 8.85-6 11.85H7L4.5 5.05l4.2-.4L10 16.45c1.1-2.05 2.45-5.3 2.45-7.5 0-1.2-.2-2-.55-2.65l4.6-1z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#3D95CE" mask="url(#venmoMask)" />
            </svg>
        );
        case 'paypal': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="paypalMask">
                        <rect width="24" height="24" fill="white" />
                        <path d="M9.5 17l.25-1.6H8l1.15-7.4H12.4c1.6 0 2.6.6 2.6 2.1 0 2.05-1.4 3.1-3.3 3.1h-1L10.1 17H9.5zm1.45-4.9h.85c.85 0 1.6-.35 1.6-1.35 0-.65-.42-1-1.15-1h-.85l-.45 2.35z" fill="black" />
                        <path d="M13.9 13.1c0 2.05-1.4 3.1-3.3 3.1h-.85l-.5 2h-1.4l1.3-8.4h2.9c1.65 0 2.65.65 2.65 2.25 0 .37-.07.75-.2 1.1l-.6-.05z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#009CDE" mask="url(#paypalMask)" />
            </svg>
        );
        case 'reddit': return (
            <svg viewBox="0 0 24 24" width={32} height={32}>
                <defs>
                    <mask id="redditMask">
                        <rect x="0" y="0" width="24" height="24" fill="white" />
                        <circle cx="12" cy="13" r="5.5" fill="black" />
                        <circle cx="7.5" cy="7.5" r="1.5" fill="black" />
                        <circle cx="16.5" cy="7.5" r="1.5" fill="black" />
                        <path d="M12 8.5c-1.8 0-3.3.5-4.5 1.3A2 2 0 006 9a2 2 0 00-.5 3.8A5 5 0 006 13.5C6 16.5 8.7 19 12 19s6-2.5 6-5.5a5 5 0 00.5-1.2A2 2 0 0018 9a2 2 0 00-1.5.8C15.3 9 13.8 8.5 12 8.5z" fill="black" />
                    </mask>
                </defs>
                <circle cx="12" cy="12" r="10" fill="#FF4500" mask="url(#redditMask)" />
                <circle cx="9.8" cy="13.2" r="1.1" fill="#FF4500" />
                <circle cx="14.2" cy="13.2" r="1.1" fill="#FF4500" />
                <path d="M10.2 15.8a3 3 0 003.6 0" stroke="#FF4500" strokeWidth="0.9" fill="none" strokeLinecap="round" />
                <circle cx="18.5" cy="7" r="2" fill="#FF4500" />
                <path d="M14.5 8c1-1.5 2.5-2 4-1.5" stroke="#FF4500" strokeWidth="0.8" fill="none" />
                <path d="M19 4.5a1 1 0 00-2 0v2.5a1 1 0 002 0" fill="#FF4500" />
            </svg>
        );
        case 'phone': return (
            <svg viewBox="0 0 24 24" width={30} height={30}>
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="#34D399" />
            </svg>
        );
        case 'email': return (
            <svg viewBox="0 0 28 22" width={34} height={26}>
                <defs>
                    <mask id="emailMask">
                        <rect width="28" height="22" fill="white" />
                        <path d="M2 3l12 9 12-9" stroke="black" strokeWidth="2" fill="none" strokeLinecap="round" />
                        <path d="M2 3h24v16H2z" fill="none" stroke="black" strokeWidth="1.5" />
                    </mask>
                </defs>
                <rect width="28" height="22" rx="4" fill="#60A5FA" mask="url(#emailMask)" />
            </svg>
        );
        case 'spotify': return (
            <svg viewBox="0 0 24 24" width={32} height={32} fill="#1DB954">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
            </svg>
        );
        case 'discord': return (
            <svg viewBox="0 0 24 24" width={32} height={32} fill="#5865F2">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
            </svg>
        );
        case 'kick': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#53fc18" />
                <text x="50%" y="50%" fill="black" fontSize="18" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle" dy=".3em">K</text>
            </svg>
        );
        case 'rebelity': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#000000" />
                <text x="50%" y="50%" fill="white" fontSize="18" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle" dy=".3em">R</text>
            </svg>
        );
        case 'eventbrite': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <circle cx="16" cy="16" r="16" fill="#F05537" />
                <text x="50%" y="50%" fill="white" fontSize="18" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle" dy=".3em">e</text>
            </svg>
        );
        case 'tumblr': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#36465D" />
                <text x="50%" y="50%" fill="white" fontSize="20" fontWeight="bold" fontFamily="serif" textAnchor="middle" dy=".3em">t</text>
            </svg>
        );
        case 'bluesky': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#0085ff" />
                <text x="50%" y="50%" fill="white" fontSize="14" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle" dy=".3em">bsky</text>
            </svg>
        );
        case 'etsy': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#F1641E" />
                <text x="50%" y="50%" fill="white" fontSize="20" fontFamily="serif" textAnchor="middle" dy=".3em">E</text>
            </svg>
        );
        case 'snapchat': return (
            <svg viewBox="0 0 32 32" width={32} height={32}>
                <rect width="32" height="32" rx="6" fill="#FFFC00" />
                <text x="50%" y="50%" fill="black" fontSize="18" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle" dy=".3em">S</text>
            </svg>
        );
        default: return null;
    }
};

const getSocialLink = (key: string, val: string, prefix: string) => {
    if (!val) return '';
    const clean = val.trim();
    if (clean.startsWith('http://') || clean.startsWith('https://')) {
        return clean;
    }
    if (key === 'youtube') {
        return `https://youtube.com/${clean.startsWith('@') ? clean : '@' + clean}`;
    }
    if (key === 'cashapp') {
        return `https://cash.app/$${clean.replace(/^\$/, '')}`;
    }
    if (key === 'whatsapp') {
        return `https://wa.me/${clean.replace(/\+/g, '').replace(/[\s-()]/g, '')}`;
    }
    return `${prefix}${clean.replace(/^@/, '')}`;
};

interface ProfileHeaderProps {
    userData: UserData;
    userId: string;
    isOwner: boolean;
    onProfilePictureChange: (url: string) => void;
    onProfileBgChange: (bg: { url: string, style: string }) => void;
    onStartConversation: (userId: string) => void;
    postCount: number;
    onFollowToggle: (userId: string) => void;
    onShowFollowers: () => void;
    onShowFollowing: () => void;
    onShowConnections: () => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onRemoveFollower?: (userId: string) => void;
    onShare?: (post: any) => void;
    canSeeComments: boolean;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    onNavigateToYipYap?: (config: { name: string, mode: 'broadcast' | 'anonymous' | 'forum', visibility: 'public' | 'open', groupIds?: string[] }) => void;
    existingNonAnonymousYipYapNames?: string[];
    onNavigateToProfile?: (userId: string) => void;
    onNavigateToHashtag?: (tag: string) => void;
    showPassionOverlay?: boolean;
    onPfpClick?: () => void;
}

export const ProfileHeader = ({ userData, userId, isOwner, onProfilePictureChange, onProfileBgChange, onStartConversation, postCount, onFollowToggle, onShowFollowers, onShowFollowing, onShowConnections, onGrantAdmin, onDeleteUser, onMuteUser, onBlockUser, onRemoveFollower, onShare, canSeeComments, onInternalModalStateChange, closeInternalModalsTrigger = 0, onNavigateToYipYap, existingNonAnonymousYipYapNames, onNavigateToProfile, onNavigateToHashtag, showPassionOverlay = false, onPfpClick }: ProfileHeaderProps) => {
    const pfpFileInputRef = useRef<HTMLInputElement>(null);
    const bgFileInputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [isBgMenuOpen, setIsBgMenuOpen] = useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [showYipYapSelector, setShowYipYapSelector] = useState(false);
    const [isSocialsModalOpen, setIsSocialsModalOpen] = useState(false);
    const [isPlaylistModalOpen, setIsPlaylistModalOpen] = useState(false);
    const optionsRef = useRef<HTMLDivElement>(null);
    const [isPassionValid, setIsPassionValid] = useState(false);

    useEffect(() => {
        if (!userData?.passion) {
            setIsPassionValid(false);
            return;
        }

        let formattedUrl = userData.passion;
        if (!userData.passion.startsWith('http')) {
            formattedUrl = `https://${userData.passion}`;
        }

        let isValidFormat = true;
        try {
            new URL(formattedUrl);
        } catch {
            isValidFormat = false;
        }

        if (!isValidFormat) {
            setIsPassionValid(false);
            return;
        }

        api.post('metadata', { url: formattedUrl })
            .then(response => {
                if (response.data && !response.data.error) {
                    setIsPassionValid(true);
                } else {
                    setIsPassionValid(false);
                }
            })
            .catch(() => setIsPassionValid(false));
    }, [userData?.passion]);

    // Close options menu when clicking outside
    useEffect(() => {
        if (!isOptionsOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target as Node)) {
                setIsOptionsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOptionsOpen]);
    //const styleOptions = ['stretch', 'span', 'tile', 'center', 'fixed'];
    const styleOptions = ['tile'];
    const isCustomNickname = userData?.followerNickname && userData.followerNickname.trim() !== '' && userData.followerNickname.toLowerCase() !== 'followers';
    const friendsLabel = isCustomNickname ? userData.followerNickname : 'Friends';
    const { currentUser, usersData, isDemo } = useAuth();
    const { audioIsPlaying } = useAudioPlayerState() || {};

    const socials = userData?.socials || {};
    const inlinePlatformsList = [
        { key: 'instagram', label: 'Instagram', prefix: 'https://instagram.com/' },
        { key: 'tiktok', label: 'TikTok', prefix: 'https://tiktok.com/@' },
        { key: 'whatsapp', label: 'WhatsApp', prefix: 'https://wa.me/' },
        { key: 'facebook', label: 'Facebook', prefix: 'https://facebook.com/' },
        { key: 'twitter', label: 'Twitter', prefix: 'https://twitter.com/' },
        { key: 'threads', label: 'Threads', prefix: 'https://threads.net/@' },
        { key: 'appleMusic', label: 'Apple Music', prefix: 'https://music.apple.com/profile/' },
        { key: 'soundcloud', label: 'SoundCloud', prefix: 'https://soundcloud.com/' },
        { key: 'pinterest', label: 'Pinterest', prefix: 'https://pinterest.com/' },
        { key: 'youtube', label: 'YouTube', prefix: 'https://youtube.com/' },
        { key: 'cashapp', label: 'Cash App', prefix: 'https://cash.app/$' },
        { key: 'twitch', label: 'Twitch', prefix: 'https://twitch.tv/' },
        { key: 'venmo', label: 'Venmo', prefix: 'https://venmo.com/' },
        { key: 'paypal', label: 'PayPal', prefix: 'https://paypal.me/' },
        { key: 'reddit', label: 'Reddit', prefix: 'https://reddit.com/user/' },
        { key: 'spotify', label: 'Spotify', prefix: 'https://open.spotify.com/user/' },
        { key: 'discord', label: 'Discord', prefix: 'https://discord.com/users/' },
        { key: 'snapchat', label: 'Snapchat', prefix: 'https://snapchat.com/add/' },
        { key: 'tumblr', label: 'Tumblr', prefix: 'https://tumblr.com/' },
        { key: 'bluesky', label: 'Bluesky', prefix: 'https://bsky.app/profile/' },
        { key: 'kick', label: 'Kick', prefix: 'https://kick.com/' },
        { key: 'etsy', label: 'Etsy', prefix: 'https://etsy.com/shop/' },
        { key: 'eventbrite', label: 'Eventbrite', prefix: 'https://eventbrite.com/' },
        { key: 'rebelity', label: 'Rebelity', prefix: 'https://rebelity.com/' }
    ].filter(p => !!socials[p.key as keyof typeof socials]);

    const visibleInline = inlinePlatformsList.slice(0, 7);
    const hiddenPlatforms = inlinePlatformsList.slice(7);
    const hasPhone = !!socials.phone;
    const hasEmail = !!socials.email;
    const hasOverflow = hiddenPlatforms.length > 0 || hasPhone || hasEmail;
    const hiddenCount = hiddenPlatforms.length + (hasPhone ? 1 : 0) + (hasEmail ? 1 : 0);

    const [cropImage, setCropImage] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);

    useEffect(() => {
        onInternalModalStateChange?.(!!(isBgMenuOpen || isOptionsOpen || isCropping || showYipYapSelector || isSocialsModalOpen));
    }, [isBgMenuOpen, isOptionsOpen, isCropping, showYipYapSelector, isSocialsModalOpen, onInternalModalStateChange]);

    useEffect(() => {
        if (closeInternalModalsTrigger > 0) {
            setIsBgMenuOpen(false);
            setIsOptionsOpen(false);
            setIsCropping(false);
            setCropImage(null);
            setIsSocialsModalOpen(false);
        }
    }, [closeInternalModalsTrigger]);

    const handlePfpClick = () => {
        if (userData?.passion && isPassionValid) {
            onPfpClick?.();
            return;
        }
        if (!isOwner) return;
        pfpFileInputRef.current?.click();
    };

    const handlePfpFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUser) {
            const reader = new FileReader();
            reader.onload = () => {
                setCropImage(reader.result as string);
                setIsCropping(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = async (croppedBlob: Blob) => {
        if (!currentUser) return;

        try {
            console.log('[DEBUG-PFP] Starting crop complete handler...');
            // Create a local blob URL for immediate display and background upload
            const localUrl = URL.createObjectURL(croppedBlob);

            // Wait for App.tsx to handle the upload and server update
            // We await this so the cropper modal stays in "Processing..." state until fully done
            await onProfilePictureChange(localUrl);

            console.log('[DEBUG-PFP] Upload and sync completed via App.tsx. Closing modal.');
            // Close modal
            setIsCropping(false);
            setCropImage(null);
        } catch (e) {
            console.error("[DEBUG-PFP] Failed to process cropped profile picture", e);
            alert("Failed to process profile picture. " + (e instanceof Error ? e.message : "Please try again."));
            // Close modal even on error to prevent being stuck
            setIsCropping(false);
            setCropImage(null);
        }
    };

    const handleBgFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUser) {
            try {
                const isGif = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');
                let fileToUpload = file;
                if (!isGif) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed, using original", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);
                onProfileBgChange({ ...(userData?.profileBg || {}), url: url });
            } catch (e) {
                console.error("Failed to update profile background", e);
                alert("Failed to update profile background.");
            }
        }
    };

    const handleBgStyleChange = (style: string) => {
        onProfileBgChange({ ...(userData?.profileBg || {}), style: style });
        setIsBgMenuOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isBgMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsBgMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isBgMenuOpen]);

    if (!userData) {
        return <div className="text-center text-gray-400 py-20 font-bold uppercase tracking-widest text-[10px]">Loading profile details...</div>;
    }

    const username = Object.keys(usersData).find(key => usersData[key] === userData) || 'user';
    const isFollowingMe = currentUser ? (usersData[currentUser]?.followers || []).includes(username) : false;
    const isFollowing = (currentUser && usersData[currentUser]) ? (usersData[currentUser].following || []).includes(username) : false;
    const isRequested = currentUser && (userData.friendRequests?.includes(currentUser) || userData.followerRequests?.includes(currentUser));
    const isFriends = (currentUser && usersData[currentUser]) ? (usersData[currentUser].friends || []).includes(username) : false;

    const headerTextColor = userData.appTextColor || '#ffffff';
    const headerFontFamily = userData.appFontFamily || undefined;
    const profileThemeColor = userData.appThemeColor || null;
    const socialOutlineRgba = profileThemeColor ? hexToRgba(profileThemeColor, 65) : null;
    const socialStickerStyle: React.CSSProperties = socialOutlineRgba
        ? { '--sticker-outline-color': socialOutlineRgba } as React.CSSProperties
        : {};

    const followButtonBg = hexToRgba(userData.appThemeColor || '#161b22', userData.postBlurOpacity ?? 50) || 'var(--app-theme-bg)';
    const followButtonTextColor = userData.appTextColor || '#ffffff';

    // Add a pulsating ring animation style inline if not in CSS file
    const passionPulseClass = (userData.passion && isPassionValid)
        ? "ring-4 ring-blue-500/80 animate-pulse transition-shadow"
        : "";

    return (
        <div className="mb-2 pt-[4vh] md:pt-6" style={{ fontFamily: headerFontFamily }}>
            <div className="max-w-5xl mx-auto px-4 relative">
                <div className="flex items-end">
                    <div className={`relative group flex-shrink-0 z-20 rounded-full ${isOwner ? 'cursor-pointer' : ''} ${passionPulseClass}`} style={{ width: '128px', height: '128px', minWidth: '128px', minHeight: '128px' }} onClick={handlePfpClick}>
                        <img src={userData.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(userId) + '&backgroundColor=b6e3f4'} alt="Profile" className="w-full h-full rounded-full object-cover group-hover:opacity-75 transition-opacity bg-transparent" onError={(e) => { e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(userId) + '&backgroundColor=b6e3f4'; }} />
                        {isOwner && (
                            <>
                                {userData?.passion ? (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            pfpFileInputRef.current?.click();
                                        }}
                                        className="absolute bottom-0 left-0 p-2 text-white rounded-full shadow-lg transition-all active:scale-95 z-30"
                                        title="Change Profile Picture"
                                    >
                                        <CameraIcon className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
                                        <CameraIcon className="w-8 h-8 text-white" />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={pfpFileInputRef}
                                    onChange={handlePfpFileChange}
                                    accept="image/*"
                                    className="hidden"
                                    aria-label="Upload profile picture"
                                />
                            </>
                        )}
                    </div>
                    {!showPassionOverlay && (
                        <div className="ml-2 mb-2">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h1 className="text-lg font-bold flex items-center gap-2 flex-wrap" style={{ color: headerTextColor, ...stickerTextStyle(0.35) }}>
                                    {userData.displayName}
                                    <span className="text-xs font-bold opacity-70" style={{ color: headerTextColor, ...stickerTextStyle(0.3) }}>@{username.toLowerCase()}</span>
                                </h1>
                                {userData.isVendor && userData.vendorName && (
                                    <div
                                        className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider animate-pulse flex items-center gap-1 border border-green-500/30 bg-green-500/10 text-green-400 shadow-[0_0_12px_rgba(34,197,94,0.15)]"
                                        style={stickerTextStyle(0.15)}
                                    >
                                        🏪 Storefront
                                    </div>
                                )}
                                {isOwner && (
                                    <button
                                        onClick={async () => {
                                            const newUsername = prompt("Enter new username (3-20 chars, letters/numbers/_/. only):\nNOTE: You can only change this once every 30 days.");
                                            if (newUsername && newUsername !== username) {
                                                if (newUsername.length < 3 || newUsername.length > 20) {
                                                    alert("Username must be between 3 and 20 characters.");
                                                    return;
                                                }
                                                if (!/^[a-zA-Z0-9_.]+$/.test(newUsername)) {
                                                    alert("Username can only contain letters, numbers, underscores, and dots.");
                                                    return;
                                                }

                                                if (!window.confirm(`Are you sure you want to change your username to @${newUsername}?\n\nThis will update all your past posts and interactions.`)) return;

                                                try {
                                                    // We use the generic updateUserOnServer which calls PUT /api/users/:username
                                                    // The backend now handles the "grand rename" logic if 'username' is in the body.
                                                    await updateUserOnServer(username, { username: newUsername });
                                                    alert("Username changed successfully! The page will now reload.");
                                                    // Force reload is the safest way to handle a primary key change in this app architecture
                                                    window.location.reload();
                                                } catch (e: any) { // Use any to access response
                                                    console.error("Failed to change username", e);
                                                    // Show the specific error from the server (e.g. "Too soon" or "Taken")
                                                    const msg = e.response?.data?.error || "Failed to change username. It might be taken or you're on cooldown.";
                                                    alert(msg);
                                                }
                                            }
                                        }}
                                        className="p-1 hover:bg-white/20 rounded-full transition-colors text-white/70"
                                        title="Change Username"
                                    >
                                        <EditIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center flex-nowrap gap-1 whitespace-nowrap overflow-x-auto no-scrollbar pb-1" style={{ color: headerTextColor }}>
                                <StickerText className="text-xs text-center font-bold flex-shrink-0" whiteOpacity={0.3}>{postCount} Posts</StickerText>
                                <StickerText className="text-xs font-bold flex-shrink-0" whiteOpacity={0.3}>&#8226;</StickerText>
                                {/* Friends count — always visible */}
                                <button onClick={onShowFollowers} className="text-xs font-bold hover:underline flex-shrink-0" style={{ color: headerTextColor, ...stickerTextStyle(0.3) }}><b>{(userData.friends || []).length}</b> {friendsLabel}</button>
                                {/* Connections button — owner only, shows non-friend followers + following */}
                                {isOwner && (<StickerText className="text-xs font-bold flex-shrink-0" whiteOpacity={0.3}>&#8226;</StickerText>)}
                                {isOwner && (<button onClick={onShowConnections} className="text-xs font-bold hover:underline flex-shrink-0" style={{ color: headerTextColor, ...stickerTextStyle(0.3) }}>Connections</button>)}
                            </div>
                            <div className="flex items-center flex-nowrap text-center gap-1 font-bold text-sm text-yellow-400 mt-2 whitespace-nowrap overflow-x-auto no-scrollbar pb-1 px-4 -mx-4">
                                <StickerText className="flex-shrink-0" whiteOpacity={0.3}>{userData.totalPoints || 0} Lyfe pts</StickerText>
                                {(isOwner || (userData.playlist && userData.playlist.length > 0)) && (
                                    <>
                                        <StickerText className="text-xs font-bold flex-shrink-0" whiteOpacity={0.3}>&#8226;</StickerText>
                                        <button
                                            onClick={() => setIsPlaylistModalOpen(true)}
                                            className={`relative flex-shrink-0 flex items-center gap-1 text-sm text-yellow-400 hover:underline transition-all hover:scale-105 active:scale-95 cursor-pointer bg-transparent border-none outline-none font-bold ${audioIsPlaying ? 'animate-boombox' : ''}`}
                                            style={stickerTextStyle(0.3)}
                                        >
                                            Now Playing
                                            {audioIsPlaying && (
                                                <>
                                                    <span className="music-note" style={{ '--mx': '20px', '--mr': '30deg', left: '10%', top: '50%' } as any}>🎵</span>
                                                    <span className="music-note" style={{ '--mx': '-15px', '--mr': '-20deg', left: '80%', top: '20%', animationDelay: '0.2s' } as any}>🎶</span>
                                                    <span className="music-note" style={{ '--mx': '10px', '--mr': '45deg', left: '50%', top: '10%', animationDelay: '0.4s' } as any}>🎵</span>
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {!showPassionOverlay && (
                    <>
                        {/* Full-width social links strip — above bio */}
                        {(inlinePlatformsList.length > 0 || true) && (
                            <div className="w-full mt-4 px-3">
                                <div className="flex items-center justify-evenly w-full gap-1">
                                    <button
                                        onClick={() => {
                                            const baseOrigin = typeof window !== 'undefined' && ((window as any).Capacitor !== 'undefined' || window.location.hostname === 'localhost') ? 'https://goodlyfe.net' : (typeof window !== 'undefined' ? window.location.origin : '');
                                            const profileUrl = `${baseOrigin}/profile/${encodeURIComponent(username)}`;
                                            const shareText = `Follow me on GoodLyfe! ${profileUrl}`;

                                            if (onShare) {
                                                onShare({
                                                    id: Date.now(),
                                                    author: userId,
                                                    type: 'profile' as any,
                                                    content: { text: '', username },
                                                    timestamp: new Date().toISOString(),
                                                    likes: 0,
                                                    comments: [],
                                                    visibility: 'public'
                                                });
                                            } else {
                                                const isPhone = typeof window !== 'undefined' && window.innerWidth <= 1024;
                                                try {
                                                    if (isPhone && (navigator as any).share) {
                                                        (navigator as any).share({
                                                            title: 'Follow me on GoodLyfe!',
                                                            text: 'Follow me on GoodLyfe!',
                                                            url: profileUrl
                                                        });
                                                    } else {
                                                        copyToClipboard(shareText);
                                                        alert('Link copied to clipboard!');
                                                    }
                                                } catch (e) { }
                                            }
                                        }}
                                        className="transition-transform hover:scale-125 active:scale-90 sticker-outline relative flex items-center justify-center flex-shrink-0"
                                        style={{ width: 32, height: 32, ...socialStickerStyle }}
                                        title="Share Profile"
                                    >
                                        <SendIcon className="w-full h-full text-white" />
                                        <img src={userData.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(userId) + '&backgroundColor=b6e3f4'} className="absolute -bottom-1 -right-1 w-[14px] h-[14px] rounded-full object-cover bg-black" style={{ border: '1px solid black' }} alt="pfp" onError={(e) => { e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(userId) + '&backgroundColor=b6e3f4'; }} />
                                    </button>
                                    {visibleInline.map((p) => {
                                        const url = getSocialLink(p.key, socials[p.key as keyof typeof socials] || '', p.prefix);
                                        return (
                                            <a
                                                key={p.key}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="transition-transform hover:scale-125 active:scale-90 sticker-outline"
                                                title={p.label}
                                                style={{ ...socialStickerStyle }}
                                            >
                                                {getSocialIcon(p.key)}
                                            </a>
                                        );
                                    })}
                                    {hasOverflow && (
                                        <button
                                            onClick={() => setIsSocialsModalOpen(true)}
                                            className="text-sm font-black opacity-60 hover:opacity-100 transition-all hover:scale-110"
                                            style={{ color: headerTextColor, ...stickerTextStyle(0.3) }}
                                            title="More links & contact info"
                                        >
                                            +{hiddenCount}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {userData.bio && (
                            <div className="mt-4 text-center max-w-2xl mx-auto px-3 text-sm whitespace-pre-wrap" style={{ color: headerTextColor, ...stickerTextStyle(0.3) }}>
                                <FormattedText
                                    text={userData.bio}
                                    onNavigateToProfile={onNavigateToProfile}
                                    onNavigateToHashtag={onNavigateToHashtag}
                                    fontSize={14}
                                />
                            </div>
                        )}

                        {isOwner && (
                            <div className="flex justify-center md:justify-start items-center gap-4 mt-3 px-3">
                                <button
                                    onClick={() => window.dispatchEvent(new CustomEvent('app.openFollowerGroups'))}
                                    style={{
                                        backgroundColor: followButtonBg,
                                        color: followButtonTextColor,
                                        border: '1.5px solid transparent',
                                        ...stickerTextStyle(0.3)
                                    }}
                                    className="font-semibold py-2 px-4 rounded-full transition-all hover:brightness-110 active:scale-95"
                                >
                                    Edit Groups
                                </button>
                                <button
                                    onClick={() => setShowYipYapSelector(true)}
                                    style={{
                                        backgroundColor: followButtonBg,
                                        color: followButtonTextColor,
                                        border: '1.5px solid transparent',
                                        ...stickerTextStyle(0.3)
                                    }}
                                    className="font-semibold py-2 px-4 rounded-full transition-all hover:brightness-110 active:scale-95"
                                >
                                    YipYap
                                </button>
                                <div className="relative" ref={optionsRef}>
                                    <button
                                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                                        style={{
                                            backgroundColor: followButtonBg,
                                            color: followButtonTextColor,
                                            border: '1.5px solid transparent',
                                        }}
                                        className="p-2 rounded-full transition-all hover:brightness-110 active:scale-95"
                                        title="More Options"
                                    >
                                        <MoreHorizontalIcon className="w-6 h-6" />
                                    </button>
                                    {isOptionsOpen && (
                                        <div
                                            style={{
                                                backgroundColor: followButtonBg,
                                                borderColor: 'rgba(255, 255, 255, 0.15)'
                                            }}
                                            className="absolute top-full right-0 mt-2 w-48 backdrop-blur-xl rounded-2xl shadow-2xl border py-1.5 z-50 animate-fade-in-fast overflow-hidden text-left"
                                        >
                                            <button
                                                onClick={() => {
                                                    window.dispatchEvent(new CustomEvent('app.startMassEditMode', { detail: { action: 'edit' } }));
                                                    setIsOptionsOpen(false);
                                                }}
                                                style={{ color: followButtonTextColor }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                            >
                                                <EditIcon className="w-4 h-4" />
                                                <span className="text-sm font-medium">Edit Posts</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    window.dispatchEvent(new CustomEvent('app.startMassEditMode', { detail: { action: 'archive' } }));
                                                    setIsOptionsOpen(false);
                                                }}
                                                style={{ color: followButtonTextColor }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                            >
                                                <BookmarkIcon className="w-4 h-4" />
                                                <span className="text-sm font-medium">Mass Archive</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    window.dispatchEvent(new CustomEvent('app.startMassEditMode', { detail: { action: 'delete' } }));
                                                    setIsOptionsOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3"
                                            >
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                                <span className="text-sm font-medium">Mass Delete</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {!isOwner && (
                            <div className="flex justify-center md:justify-start items-center gap-4 mt-3 px-3">
                                <button onClick={() => onFollowToggle(username)}
                                    style={{
                                        backgroundColor: followButtonBg,
                                        color: followButtonTextColor,
                                        border: (isFriends || isFollowing || isRequested)
                                            ? `1.5px solid ${followButtonTextColor}`
                                            : '1.5px solid transparent',
                                        ...stickerTextStyle(0.3)
                                    }}
                                    className="font-semibold py-2 px-4 rounded-full transition-all hover:brightness-110 active:scale-95"
                                >
                                    {isFriends ? friendsLabel : isFollowing ? 'Following' : isRequested ? 'Requested' : 'Add Friend'}
                                </button>
                                <button onClick={() => onStartConversation(username)}
                                    style={{
                                        backgroundColor: followButtonBg,
                                        color: followButtonTextColor,
                                        border: '1.5px solid transparent',
                                        ...stickerTextStyle(0.3)
                                    }}
                                    className="font-semibold py-2 px-4 rounded-full transition-all hover:brightness-110 active:scale-95"
                                >
                                    Message
                                </button>

                                <div className="relative" ref={optionsRef}>
                                    <button
                                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                                        style={{
                                            backgroundColor: followButtonBg,
                                            color: followButtonTextColor,
                                            border: '1.5px solid transparent',
                                        }}
                                        className="p-2 rounded-full transition-all hover:brightness-110 active:scale-95"
                                        title="More Options"
                                    >
                                        <MoreHorizontalIcon className="w-6 h-6" />
                                    </button>
                                    {isOptionsOpen && (
                                        <div
                                            style={{
                                                backgroundColor: followButtonBg,
                                                borderColor: 'rgba(255, 255, 255, 0.15)'
                                            }}
                                            className="absolute top-full right-0 mt-2 w-48 backdrop-blur-xl rounded-2xl shadow-2xl border py-1.5 z-50 animate-fade-in-fast overflow-hidden text-left"
                                        >
                                            {isFollowingMe && (
                                                <button
                                                    onClick={() => {
                                                        window.dispatchEvent(new CustomEvent('app.openFollowerGroups', { detail: { targetUser: username } }));
                                                        setIsOptionsOpen(false);
                                                    }}
                                                    style={{ color: followButtonTextColor }}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                                >
                                                    <PlusIcon className="w-4 h-4" />
                                                    <span className="text-sm font-medium">Categorize Follower</span>
                                                </button>
                                            )}
                                            {isFollowingMe && onRemoveFollower && (
                                                <button
                                                    onClick={() => { onRemoveFollower(username); setIsOptionsOpen(false); }}
                                                    style={{ color: followButtonTextColor }}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                                >
                                                    <BrokenHeartIcon className="w-4 h-4" />
                                                    <span className="text-sm font-medium">Remove Follower</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => { onMuteUser && onMuteUser(username); setIsOptionsOpen(false); }}
                                                style={{ color: followButtonTextColor }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                            >
                                                <VolumeOffIcon className="w-4 h-4" />
                                                <span className="text-sm font-medium">
                                                    {usersData[currentUser]?.mutedUsers?.includes(username) ? 'Unmute' : 'Mute'} @{username}
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => { onBlockUser && onBlockUser(username); setIsOptionsOpen(false); }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3"
                                            >
                                                <BrokenHeartIcon className="w-4 h-4 text-red-500" />
                                                <span className="text-sm font-medium">
                                                    {usersData[currentUser]?.blockedUsers?.includes(username) ? 'Unblock' : 'Block'} @{username}
                                                </span>
                                            </button>
                                            {currentUser && usersData[currentUser]?.role === 'admin' && onGrantAdmin && username.toLowerCase() !== 'agk' && (
                                                <button
                                                    onClick={() => { onGrantAdmin(username); setIsOptionsOpen(false); }}
                                                    style={{ color: followButtonTextColor }}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3 border-t border-white/10 mt-1"
                                                >
                                                    <PrivateIcon className="w-4 h-4" />
                                                    <span className="text-sm font-medium">
                                                        {userData.role === 'admin' ? 'Revoke Admin' : 'Grant Admin'}
                                                    </span>
                                                </button>
                                            )}
                                            {currentUser && usersData[currentUser]?.role === 'admin' && onDeleteUser && username.toLowerCase() !== 'agk' && (
                                                <button
                                                    onClick={() => {
                                                        setIsOptionsOpen(false);
                                                        const confirmName = prompt(`⚠️ DANGER: Verify deletion.\n\nType "${username}" to confirm permanent deletion of this user and ALL their data.`);
                                                        if (confirmName === username) {
                                                            onDeleteUser(username);
                                                        } else if (confirmName !== null) {
                                                            alert("Username mismatch. Deletion cancelled.");
                                                        }
                                                    }}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-500 transition-colors flex items-center gap-3"
                                                >
                                                    <TrashIcon className="w-4 h-4 text-red-500" />
                                                    <span className="text-sm font-medium">Delete User</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            {isCropping && cropImage && (
                <PfpCropperModal
                    image={cropImage}
                    onClose={() => { setIsCropping(false); setCropImage(null); }}
                    onCropComplete={handleCropComplete}
                />
            )}
            <YipYapCreateModal
                isOpen={showYipYapSelector}
                onClose={() => setShowYipYapSelector(false)}
                onSubmit={(data) => {
                    onNavigateToYipYap?.(data);
                    setShowYipYapSelector(false);
                }}
                existingNonAnonymousYipYapNames={existingNonAnonymousYipYapNames}
                followerGroups={userData.followerGroups}
                themeColor={userData.appThemeColor}
                textColor={userData.appTextColor}
                opacity={userData.postBlurOpacity}
            />

            {isSocialsModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-md"
                        onClick={() => setIsSocialsModalOpen(false)}
                    />

                    {/* Content Box */}
                    <div
                        className="relative w-full max-w-md rounded-3xl p-6 overflow-hidden animate-scale-up"
                        style={{
                            backgroundColor: hexToRgba(userData.appThemeColor || '#161b22', userData.postBlurOpacity ?? 50) || 'var(--app-theme-bg)',
                            color: userData.appTextColor || '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.5)'
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 pb-2 border-b border-white/10">
                            <h3 className="text-md font-black uppercase tracking-wider text-white">Links & Contacts</h3>
                            <button
                                onClick={() => setIsSocialsModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors text-white"
                            >
                                <CloseIcon className="w-4 h-4" />
                            </button>
                        </div>

                        {/* List */}
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 no-scrollbar">
                            {/* Contact section if present */}
                            {(hasPhone || hasEmail) && (
                                <div className="space-y-3">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white/40">Contact Information</h4>
                                    <div className="space-y-2">
                                        {hasPhone && (
                                            <a
                                                href={`tel:${socials.phone}`}
                                                className="w-full p-3 rounded-2xl bg-white/5 flex items-center gap-3 hover:bg-white/10 transition-all hover:translate-x-1"
                                            >
                                                <span className="flex-shrink-0 sticker-outline" style={socialStickerStyle}>{getSocialIcon('phone')}</span>
                                                <div className="text-left">
                                                    <div className="text-[9px] font-bold uppercase text-white/50">Phone</div>
                                                    <div className="text-xs font-semibold text-white">{socials.phone}</div>
                                                </div>
                                            </a>
                                        )}
                                        {hasEmail && (
                                            <a
                                                href={`mailto:${socials.email}`}
                                                className="w-full p-3 rounded-2xl bg-white/5 flex items-center gap-3 hover:bg-white/10 transition-all hover:translate-x-1"
                                            >
                                                <span className="flex-shrink-0 sticker-outline" style={socialStickerStyle}>{getSocialIcon('email')}</span>
                                                <div className="text-left">
                                                    <div className="text-[9px] font-bold uppercase text-white/50">Email</div>
                                                    <div className="text-xs font-semibold text-white break-all">{socials.email}</div>
                                                </div>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* All socials list */}
                            {inlinePlatformsList.length > 0 && (
                                <div className="space-y-3 mt-4">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white/40">Social Platforms</h4>
                                    <div className="grid grid-cols-1 gap-2">
                                        {inlinePlatformsList.map(p => {
                                            const val = socials[p.key as keyof typeof socials] || '';
                                            const url = getSocialLink(p.key, val, p.prefix);
                                            return (
                                                <a
                                                    key={p.key}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full p-3 rounded-2xl bg-white/5 flex items-center gap-3 hover:bg-white/10 transition-all hover:translate-x-1"
                                                >
                                                    <span className="flex-shrink-0 sticker-outline" style={socialStickerStyle}>{getSocialIcon(p.key)}</span>
                                                    <div className="text-left">
                                                        <div className="text-[9px] font-bold uppercase text-white/50">{p.label}</div>
                                                        <div className="text-xs font-semibold text-white">{val}</div>
                                                    </div>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <PlaylistModal
                isOpen={isPlaylistModalOpen}
                onClose={() => setIsPlaylistModalOpen(false)}
                profileUser={username}
                isOwner={isOwner}
            />
        </div>
    )
}
